<script lang="ts" setup>
import { Fancybox } from '@fancyapps/ui'
// import { Fancybox } from '@fancyapps/ui/dist/fancybox/fancybox.esm.js'
// import ru from '@fancyapps/ui/dist/fancybox/l10n/ru.esm.js'
import '@fancyapps/ui/dist/fancybox/fancybox.css'
import type { userSlideType } from '@fancyapps/ui/types/Carousel/types'
import { Navigation, Pagination, Swiper } from 'swiper'

import ImageType from '~/enums/image-type'
import helpers from '~/helpers'

type SwiperSlide = {
  type: 'image' | 'video'
  url: string
}

type Video = {
  thumbnailUrl: string
  url: string
}

const props = defineProps<{
  galleriesData: any[]
  imagesData: any[]
  productName: string | null
  videos: Video[]
}>()

Swiper.use([Navigation, Pagination])

const imageTypesForGallery = [
  ImageType.Original,
  ImageType.Thumbnail480p,
  ImageType.Thumbnail360p,
  ImageType.Thumbnail240p
]
const imageTypesForSlider = [
  ImageType.Thumbnail480p,
  ImageType.Thumbnail360p,
  ImageType.Thumbnail240p,
  ImageType.Original
]

const navigationNextRef = ref<HTMLElement | null>(null)
const navigationPrevRef = ref<HTMLElement | null>(null)
const sliderRef = ref<HTMLElement | null>(null)
const swiper = ref<Swiper | null>(null)

const fancyboxSlides = computed(() => [
  ...imageUrls.value.map(
    (x: object): any => ({
      src: helpers.getImageUrl(x, imageTypesForGallery),
      thumb: helpers.getImageUrl(x, imageTypesForSlider),
      type: 'image'
    } as userSlideType)
  ),
  ...props.videos.map(
    (x: Video): any => ({ src: x.url, thumb: x.thumbnailUrl, type: 'html5video' } as userSlideType)
  )
])
const imageUrls = computed(() => {
  const mainImageUrls = helpers.getImageUrls(props.imagesData)
  const imageUrls = []

  if (mainImageUrls) {
    imageUrls.push(mainImageUrls)
  }

  for (const galleryData of props.galleriesData) {
    const galleryImageUrls = helpers.getImageUrls(galleryData.relationships.images.data)

    if (galleryImageUrls) {
      imageUrls.push(galleryImageUrls)
    }
  }

  return imageUrls
})
const swiperSlides = computed(() => [
  ...imageUrls.value.map(
    (x: object): SwiperSlide => ({ type: 'image', url: helpers.getImageUrl(x, imageTypesForSlider) })
  ),
  ...props.videos.map((x: Video): SwiperSlide => ({ type: 'video', url: x.thumbnailUrl }))
])

onMounted(() => {
  swiper.value = new Swiper(
    sliderRef.value as HTMLElement,
    {
      navigation: {
        nextEl: navigationNextRef.value,
        prevEl: navigationPrevRef.value
      },
      pagination: {
        bulletActiveClass: 'bullet-dot_active',
        bulletClass: 'bullet-dot',
        el: '.swiper-pagination',
        renderBullet: () => '<div class="bullet-dot"></div>'
      },
      spaceBetween: 10
    }
  )
})

function showGalleryModalWindow (startIndex: number, isVideo: boolean): void {
  Fancybox.show(
    fancyboxSlides.value,
    {
      Carousel: { infinite: false },
      Html: { videoAutoplay: isVideo },
      Thumbs: { type: 'classic' },
      Toolbar: { display: { left: [], middle: ['infobar'], right: [] } },
      closeButton: true,
      compact: false,
      idle: false,
      // l10n: ru,
      showClass: false,
      startIndex,
      hideClass: false
    }
  )
}
</script>

<template>
  <div>
    <div ref="sliderRef" class="swiper-container product-images-gallery">
      <div class="swiper-wrapper">
        <div
          v-for="({ type, url }, i) in swiperSlides"
          :key="i"
          class="swiper-slide cursor-pointer bg-white"
          :class="type"
          @click="showGalleryModalWindow(i, type === 'video')"
        >
          <img v-lazy="url" :alt="productName" loading="lazy">
        </div>
      </div>
      <div ref="navigationPrevRef" class="swiper-button-prev swiper-button-disabled bg-white">
        <SvgIcon name="outlined/navigate-before" />
      </div>
      <div ref="navigationNextRef" class="swiper-button-next bg-white">
        <SvgIcon name="outlined/navigate-next" />
      </div>
      <div class="swiper-pagination mobile" />
    </div>

    <div class="overlay-wrapper">
      <slot name="overlay" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use 'assets/css/variables';

.swiper-pagination {
  bottom: 0;

  &.mobile {
    display: none;

    @media screen and (max-width: 1020px) {
      display: block;
    }
  }
}

.swiper {
  border-radius: 12px;
  height: 100%;
  width: 100%;
}

.swiper-slide {
  align-items: center;
  display: flex;
  justify-content: center;

  &.video:before {
    background: rgba(variables.$main-gray-color, .5) url(assets/img/icons/custom/play-circle-white.svg) no-repeat;
    border-radius: 50%;
    content: '';
    height: 48px;
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 48px;
    z-index: 1;
  }

  img {
    height: 100%;
    object-fit: contain;
    width: 100%;
  }
}

.product-images-gallery {
  height: 300px;
  width: 100%;
}

.swiper-button-prev,
.swiper-button-next {
  border: 1px solid variables.$main-light-gray-color;
  border-radius: 50%;
  box-sizing: border-box;
  color: variables.$main-blue-color;
  font-size: 24px;
  height: 40px;
  width: 40px;

  @media screen and (max-width: 1020px) {
    display: none;
  }

  &:after {
    display: none;
  }
}

.swiper-button-disabled {
  display: none;
}

.overlay-wrapper {
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
}

:deep(.bullet-dot) {
  background: white;
  border: 1px solid variables.$main-light-gray-color;
  border-radius: 50%;
  box-sizing: border-box;
  display: inline-block;
  height: 6px;
  margin-right: 4px;
  width: 6px;

  &:last-child {
    margin-right: 0;
  }
}

:deep(.bullet-dot_active) {
  background: variables.$main-light-gray-color;
  border: none;
}
</style>
